import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import { track } from '@adsk/acs-web-analytics';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { EVENTS } from './helpers/const';
import { userPropType, User } from './helpers/types';
import { getTrackingProperties } from './helpers/utils';
import { useUserSubscriptions, useTrialMessage } from './hooks';
import TrialBannerView from './TrialBannerView';

type TrialBannerProps = StylableComponent<HTMLDivElement> & {
  user?: User | null;
  currentProductId?: string;
};

/**
 * A TrialBanner
 * Trial banner allow to show messages and options to manage the subscriptions of the user.
 */

const TrialBanner = React.forwardRef<HTMLDivElement, TrialBannerProps>(
  ({ user, currentProductId, ...props }, ref) => {
    const { value: cleanLicenses, loading: cleanLicensesLoading } =
      useAlloyFeatureFlag('trial-banner-api-change', false);

    const { userTrialSubscriptions, showBanner } = useUserSubscriptions({
      user,
      cleanLicenses,
      cleanLicensesLoading,
    });

    const trialMessage = useTrialMessage({ userTrialSubscriptions });

    const trackOpenModal = useCallback(() => {
      if (!user) return null;

      return track(EVENTS.OPEN, {
        ...getTrackingProperties(
          user,
          userTrialSubscriptions,
          currentProductId ? currentProductId : 'projects',
        ),
      });
    }, [currentProductId, user, userTrialSubscriptions]);

    return userTrialSubscriptions ? (
      <TrialBannerView
        {...props}
        ref={ref}
        message={showBanner ? trialMessage : null}
        userTrials={userTrialSubscriptions}
        displayButton={showBanner}
        onClickAnalytics={trackOpenModal}
      />
    ) : null;
  },
);

TrialBanner.displayName = 'TrialBanner';

TrialBanner.defaultProps = {
  user: null,
};

TrialBanner.propTypes = {
  /** Current Product Id */
  currentProductId: PropTypes.string,
  /** Current User */
  user: userPropType,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
};

export default TrialBanner;
